// The password policy, once, for every form that sets one: the settings
// rotation and the public activation / reset pages (PRD-59 Etapa C — the
// activation page checked two of these ten rules and let the server surprise
// a first-time teacher with the rest after she tapped "Activar mi cuenta").
//
// Mirrors the backend policy service (core/password_policy.py): 12+ chars,
// lowercase, uppercase, digit, no consecutive/repeating sequences, no
// forbidden words. There is NO special-character requirement. Client-side
// rejection is a UX nicety; the server is authoritative and returns the full
// violations list.
export const PASSWORD_MIN_LENGTH = 12

// Mirrors core/password_policy._FORBIDDEN_WORDS
const _FORBIDDEN_WORDS = [
  "password",
  "contraseña",
  "admin",
  "administrator",
  "platonica",
  "cecytem",
  "ia",
  "ai",
]

export const passwordPolicy = {
  minLength: PASSWORD_MIN_LENGTH,
  hasLower: (s: string) => /[a-z]/.test(s),
  hasUpper: (s: string) => /[A-Z]/.test(s),
  hasDigit: (s: string) => /\d/.test(s),
  noConsecutiveDigits: (s: string): boolean => {
    for (let i = 0; i < s.length - 2; i++) {
      const a = s[i]!,
        b = s[i + 1]!,
        c = s[i + 2]!
      if (/\d/.test(a) && /\d/.test(b) && /\d/.test(c)) {
        const da = b.charCodeAt(0) - a.charCodeAt(0)
        const db = c.charCodeAt(0) - b.charCodeAt(0)
        if (da === db && Math.abs(da) === 1) return false
      }
    }
    return true
  },
  noConsecutiveLetters: (s: string): boolean => {
    const lower = s.toLowerCase()
    for (let i = 0; i < lower.length - 2; i++) {
      const a = lower[i]!,
        b = lower[i + 1]!,
        c = lower[i + 2]!
      if (/[a-z]/.test(a) && /[a-z]/.test(b) && /[a-z]/.test(c)) {
        const da = b.charCodeAt(0) - a.charCodeAt(0)
        const db = c.charCodeAt(0) - b.charCodeAt(0)
        if (da === db && Math.abs(da) === 1) return false
      }
    }
    return true
  },
  noRepeatingChars: (s: string) => !/(.)\1{2}/.test(s),
  noForbiddenWords: (s: string) => {
    const lower = s.toLowerCase()
    return !_FORBIDDEN_WORDS.some((w) => lower.includes(w))
  },
}

/** Each rule's verdict for a candidate password, in the order the list shows them. */
export function passwordRules(password: string) {
  return {
    length: password.length >= PASSWORD_MIN_LENGTH,
    case: passwordPolicy.hasUpper(password) && passwordPolicy.hasLower(password),
    digit: passwordPolicy.hasDigit(password),
    no_consecutive_digits: passwordPolicy.noConsecutiveDigits(password),
    no_consecutive_letters: passwordPolicy.noConsecutiveLetters(password),
    no_repeating_chars: passwordPolicy.noRepeatingChars(password),
    no_forbidden_words: passwordPolicy.noForbiddenWords(password),
  }
}
export type PasswordRuleKey = keyof ReturnType<typeof passwordRules>
